import { gsap } from 'gsap'
import { SplitText } from 'gsap/SplitText'
import { module as BaseModule } from 'modujs'

gsap.registerPlugin(SplitText)

export default class extends BaseModule {
  init () {
    this.state = false
    this.toggleBtns = document.querySelectorAll('[data-menu-toggle]')
    this.toggleBtns.forEach(btn => {
      btn.addEventListener('click', this.toggleMenu.bind(this))
    })

    this.handleDocumentClick = this.onDocumentClick.bind(this)
    this.timelineReady = this.setTimeline()
  }

  menuWidth () {
    return window.innerWidth > 900 ? '586px' : `${window.innerWidth - 16}px`
  }

  async setTimeline () {
    this.wrapper = document.querySelector('[data-menu-wrapper]')
    this.portfolioTitle = document.querySelector('[data-menu-portfolio-title]')
    this.categories = document.querySelector('[data-menu-categories]')
    this.categoryItems = gsap.utils.toArray(this.categories?.children || [])
    this.categoryLinks = gsap.utils.toArray(
      this.categories?.querySelectorAll('.animate-underline') || []
    )
    this.categoryUnderlines = this.categoryLinks
      .map(link => link.querySelector('.underline'))
      .filter(Boolean)
    this.socials = document.querySelector('[data-menu-socials]')
    this.about = document.querySelector('[data-menu-about]')
    this.aboutLink = this.about?.querySelector('.animate-underline')
    this.aboutUnderline = this.aboutLink?.querySelector('.underline')
    this.separator = document.querySelector('[data-menu-separator]')
    this.closeBtn = document.querySelector('[data-menu-close]')
    this.burgerBtn = document.querySelector('[data-module-navigation]')

    gsap.set(this.wrapper, { display: 'none' })
    this.wrapper.classList.remove('u-menu-open-menu-openen')

    gsap.set([this.socials, this.about, this.separator, this.closeBtn], {
      opacity: 0,
    })
    gsap.set(this.categoryItems, { opacity: 0, x: 40 })
    this.resetUnderlines()

    await document.fonts.ready

    // Split while measurable (display:none yields 0-height masks). Skip
    // autoSplit so opening the menu cannot orphan timeline line targets.
    if (this.portfolioTitle) {
      gsap.set(this.wrapper, {
        display: 'grid',
        visibility: 'hidden',
        width: this.menuWidth(),
        height: 'auto',
      })

      this.portfolioSplit = SplitText.create(this.portfolioTitle, {
        type: 'words,lines',
        linesClass: 'line',
        mask: 'lines',
      })
      gsap.set(this.portfolioTitle, { opacity: 1 })
      gsap.set(this.portfolioSplit.lines, { yPercent: 100, opacity: 0 })

      gsap.set(this.wrapper, {
        display: 'none',
        visibility: 'visible',
        width: 0,
        height: 0,
      })
    }

    this.timeline = gsap
      .timeline({
        paused: true,
        onComplete: () => this.enableUnderlineHover(),
        onReverseComplete: () => {
          this.resetMenuMotion()
          gsap.set(this.wrapper, { display: 'none' })
          document.body.classList.remove('has-menu')
        },
      })
      .fromTo(this.wrapper, {
        width: 0,
        height: 0,
        opacity: 1,
      }, {
        width: () => this.menuWidth(),
        height: 'auto',
        duration: 0.5,
        ease: 'power2.out',
        immediateRender: false,
      }, 0)
      .to(this.burgerBtn, {
        opacity: 0,
      }, 0)
      .fromTo(this.separator, {
        opacity: 0,
        width: 0,
      }, {
        duration: 0.5,
        opacity: 1,
        width: '100%',
        ease: 'power2.out',
      }, 0.35)

    if (this.portfolioSplit?.lines?.length) {
      // After the panel expand — otherwise the masked slide-up finishes
      // while the wrapper is still display:none / 0×0.
      this.timeline.fromTo(this.portfolioSplit.lines, {
        yPercent: 100,
        opacity: 0,
      }, {
        yPercent: 0,
        opacity: 1,
        duration: 0.9,
        stagger: 0.2,
        ease: 'power3.out',
        immediateRender: false,
      }, '>')
    }

    if (this.categoryItems.length) {
      this.timeline.to(this.categoryItems, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: 'power2.out',
      }, 0.55)
    }

    if (this.categoryUnderlines.length) {
      this.timeline.to(this.categoryUnderlines, {
        '--underline-scale': 1,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
        onStart: () => {
          this.categoryLinks.forEach(link => {
            link.classList.remove('is-underline-pending')
            link.classList.add('is-underline-in')
          })
        },
      }, 0.7)
    }

    this.timeline
      .to(this.about, {
        opacity: 1,
        duration: 1,
      }, 0.65)
      .to(this.socials, {
        opacity: 1,
      }, 0.75)
      .to(this.closeBtn, {
        opacity: 1,
      }, 0.9)

    if (this.aboutUnderline) {
      this.timeline.to(this.aboutUnderline, {
        '--underline-scale': 1,
        duration: 0.6,
        ease: 'power2.out',
        onStart: () => {
          this.aboutLink?.classList.remove('is-underline-pending')
          this.aboutLink?.classList.add('is-underline-in')
        },
      }, 0.8)
    }
  }

  resetUnderlines () {
    const links = [...this.categoryLinks, this.aboutLink].filter(Boolean)
    const underlines = [...this.categoryUnderlines, this.aboutUnderline].filter(Boolean)

    links.forEach(link => {
      link.classList.remove('is-underline-in')
      link.classList.add('is-underline-pending')
    })

    if (underlines.length) {
      gsap.set(underlines, { '--underline-scale': 0 })
    }
  }

  enableUnderlineHover () {
    const underlines = [...this.categoryUnderlines, this.aboutUnderline].filter(Boolean)

    ;[...this.categoryLinks, this.aboutLink].filter(Boolean).forEach(link => {
      link.classList.remove('is-underline-pending')
      link.classList.add('is-underline-in')
    })

    if (underlines.length) {
      gsap.set(underlines, { clearProps: '--underline-scale' })
    }
  }

  resetMenuMotion () {
    this.resetUnderlines()

    if (this.categoryItems.length) {
      gsap.set(this.categoryItems, { opacity: 0, x: 40 })
    }

    if (this.portfolioSplit?.lines?.length) {
      gsap.set(this.portfolioSplit.lines, { yPercent: 100, opacity: 0 })
    }
  }

  toggleMenu () {
    if (this.state) {
      this.closeMenu()
    } else {
      this.openMenu()
    }
  }

  async openMenu () {
    await this.timelineReady
    if (!this.timeline || this.state) return

    this.state = true
    document.body.classList.add('has-menu')
    this.el.setAttribute('aria-expanded', true)

    // Must be measurable before height:"auto" / SplitText lines animate.
    gsap.set(this.wrapper, { display: 'grid', width: 0, height: 0 })

    this.timeline.timeScale(1.15)
    this.timeline.play(0)
    document.addEventListener('pointerdown', this.handleDocumentClick)
  }

  async closeMenu () {
    if (!this.state) return
    await this.timelineReady
    if (!this.timeline) return

    this.state = false
    this.el.setAttribute('aria-expanded', false)
    // Keep has-menu until reverse completes so CSS display:none doesn't
    // hide the panel mid-close (has-menu is cleared in onReverseComplete).

    // Re-sync underline values so reverse can tween them closed
    const underlines = [...this.categoryUnderlines, this.aboutUnderline].filter(Boolean)
    if (underlines.length) {
      gsap.set(underlines, { '--underline-scale': 1 })
    }

    this.timeline.timeScale(2)
    this.timeline.reverse()
    document.removeEventListener('pointerdown', this.handleDocumentClick)
  }

  onDocumentClick (event) {
    if (!this.wrapper || !this.state) return

    const toggles = Array.from(this.toggleBtns || [])
    const safeElements = [this.wrapper, this.burgerBtn, ...toggles].filter(Boolean)
    const clickedInside = safeElements.some(element => element.contains(event.target))

    if (!clickedInside) {
      this.closeMenu()
    }
  }
}
